import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { FiArrowLeft, FiSave } from "react-icons/fi";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { makeRequestAuth } from "../../_config/api";

function CreateP2PBuyOrder() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    walletType: "USDT",
    amount: "",
    rate: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic Validation
    if (!formData.name.trim()) return showErrorToast("Name is required");
    if (!formData.amount || formData.amount <= 0)
      return showErrorToast("Valid amount is required");
    if (!formData.rate || formData.rate <= 0)
      return showErrorToast("Valid rate is required");

    const payload = {
      name: formData.name,
      walletType: formData.walletType,
      amount: Number(formData.amount),
      rate: Number(formData.rate),
    };

    try {
      setSubmitting(true);
      const response = await makeRequestAuth(
        "post",
        "/admin/createP2PBuyOrder",
        payload,
      );

      if (
        response.status === true ||
        response.success === true ||
        response.data
      ) {
        showSuccessToast("Buy order created successfully!");
        setTimeout(() => navigate("/p2p/buy-order-list"), 1500);
      } else {
        showErrorToast(response.message || "Failed to create buy order");
      }
    } catch (err) {
      console.error("Error creating buy order:", err);
      showErrorToast(
        err.response?.data?.message || err.message || "Server error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4">
      <div className="bg-white dark:bg-[#1E2738] rounded-2xl p-6 md:p-8 border border-purple-300 dark:border-purple-600/50 shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8 border-b border-gray-200 dark:border-gray-700 pb-4">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Create Admin P2P Buy Order
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Create a new buy order listing for users to sell to you.
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-purple-600 transition"
          >
            <FiArrowLeft size={20} /> Back
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                Order Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Bulk USDT Purchase"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none transition"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                Wallet Type <span className="text-red-500">*</span>
              </label>
              <select
                name="walletType"
                value={formData.walletType}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none transition cursor-pointer"
              >
                <option value="USDT">USDT</option>
                <option value="TOKEN">TOKEN</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                Amount to Buy <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="amount"
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={handleChange}
                placeholder="e.g., 1000"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none transition"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                Rate (Price per unit) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="rate"
                min="0"
                step="0.01"
                value={formData.rate}
                onChange={handleChange}
                placeholder="e.g., 87.50"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none transition"
                required
              />
            </div>
          </div>

          {/* Submit Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-2.5 rounded-xl border border-gray-300 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-8 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold shadow-lg hover:scale-[1.02] transition disabled:opacity-50"
            >
              {submitting ? (
                "Processing..."
              ) : (
                <>
                  <FiSave /> Publish Buy Order
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

export default CreateP2PBuyOrder;
